import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import './EditableCard.css';

interface EditableCardProps {
  id: string;
  content: string;
  subJobs?: string[];
  isPreview?: boolean;
  onRemove?: (id: string) => void;
  onEdit?: (id: string, newContent: string) => void;
  onEditSubJob?: (id: string, subJobIndex: number, newContent: string) => void;
  onRemoveSubJob?: (id: string, subJobIndex: number) => void;
  onAddSubJob?: (id: string) => void;
}

const EditableCard: React.FC<EditableCardProps> = ({
  id,
  content,
  subJobs,
  isPreview,
  onRemove,
  onEdit,
  onEditSubJob,
  onRemoveSubJob,
  onAddSubJob,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(content);
  const [editingSubJobIndex, setEditingSubJobIndex] = useState<number | null>(null);
  const [subJobEditValue, setSubJobEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const subJobInputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Focus subjob input when entering subjob edit mode
  useEffect(() => {
    if (editingSubJobIndex !== null && subJobInputRef.current) {
      subJobInputRef.current.focus();
      subJobInputRef.current.select();
    }
  }, [editingSubJobIndex]);

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(content);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (onEdit && editValue.trim() !== '') {
      onEdit(id, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(content);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    // Save on blur
    handleSave();
  };

  const handleAddSubJob = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddSubJob) {
      onAddSubJob(id);
    }
  };

  const handleEditSubJob = (index: number, currentValue: string) => {
    setSubJobEditValue(currentValue);
    setEditingSubJobIndex(index);
  };

  const handleSaveSubJob = () => {
    if (onEditSubJob && editingSubJobIndex !== null && subJobEditValue.trim() !== '') {
      onEditSubJob(id, editingSubJobIndex, subJobEditValue.trim());
    }
    setEditingSubJobIndex(null);
  };

  const handleCancelSubJobEdit = () => {
    setSubJobEditValue('');
    setEditingSubJobIndex(null);
  };

  const handleSubJobKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveSubJob();
    } else if (e.key === 'Escape') {
      handleCancelSubJobEdit();
    }
  };

  const handleRemoveSubJob = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (onRemoveSubJob) {
      onRemoveSubJob(id, index);
    }
  };

  return (
    <div className={`editable-card ${isPreview ? 'card-preview' : ''}`} data-card-id={id}>
      {onRemove && (
        <button
          className="card-remove-button"
          onClick={handleRemove}
          title="Delete position"
        >
          ×
        </button>
      )}
      {onEdit && !isEditing && (
        <button
          className="card-edit-button"
          onClick={handleEdit}
          title="Edit position"
        >
          ✎
        </button>
      )}
      <div className="card-content">
        {/* Main job title */}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            className="card-edit-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="card-text" onDoubleClick={handleEdit}>
            {content}
          </div>
        )}

        {/* Sub-jobs list */}
        {subJobs && subJobs.length > 0 && (
          <div className="card-subjobs">
            {subJobs.map((job, index) => (
              <div key={index} className="card-subjob-wrapper">
                {onRemoveSubJob && (
                  <button
                    className="subjob-remove-button"
                    onClick={(e) => handleRemoveSubJob(e, index)}
                    title="Delete job"
                  >
                    ×
                  </button>
                )}
                {onEditSubJob && editingSubJobIndex !== index && (
                  <button
                    className="subjob-edit-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditSubJob(index, job);
                    }}
                    title="Edit job"
                  >
                    ✎
                  </button>
                )}
                {editingSubJobIndex === index ? (
                  <input
                    ref={subJobInputRef}
                    type="text"
                    className="card-edit-input"
                    value={subJobEditValue}
                    onChange={(e) => setSubJobEditValue(e.target.value)}
                    onKeyDown={handleSubJobKeyDown}
                    onBlur={handleSaveSubJob}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div
                    className="card-subjob-text"
                    onDoubleClick={() => onEditSubJob && handleEditSubJob(index, job)}
                  >
                    {job}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add job button at bottom */}
      {onAddSubJob && (
        <button
          className="card-add-job-button"
          onClick={handleAddSubJob}
          title="Add job to this cell"
        >
          <ChevronDown size={12} />
        </button>
      )}
    </div>
  );
};

export default React.memo(EditableCard);
